/**
 * Unified Map Marker Component + Web Marker Builder
 * Renders category-based pins consistently across native and web maps.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  StyleSheet,
  Platform,
  Pressable,
  Animated,
  Easing,
  Text,
  Image,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { MapPin } from './MapView';

export type MarkerSize = 'small' | 'medium' | 'large';

export type PinVisualCategory =
  | 'accommodation'
  | 'transportation'
  | 'ferry'
  | 'flights'
  | 'food'
  | 'activities'
  | 'themeParks'
  | 'coffee'
  | 'shopping'
  | 'attractions'
  | 'parking'
  | 'gas'
  | 'hospital'
  | 'atm'
  | 'information'
  | 'nightlife'
  | 'landmarks'
  | 'generic';

type HighlightType = NonNullable<MapPin['highlight']>;

type KeywordGroup = {
  category: PinVisualCategory;
  keywords: string[];
};

type SizeConfig = {
  width: number;
  height: number;
};

export type MapMarkerProps = {
  pin: MapPin;
  size?: MarkerSize;
  selected?: boolean;
  showIndex?: boolean;
  onPress?: () => void;
};

// Icon mappings for each category
const PIN_ICONS: Record<PinVisualCategory, { name: keyof typeof Ionicons.glyphMap; color: string }> = {
  accommodation: { name: 'bed-outline', color: '#8B5CF6' },
  transportation: { name: 'bus-outline', color: '#3B82F6' },
  ferry: { name: 'boat-outline', color: '#0891B2' },
  flights: { name: 'airplane-outline', color: '#0EA5E9' },
  food: { name: 'restaurant-outline', color: '#F97316' },
  activities: { name: 'ticket-outline', color: '#10B981' },
  themeParks: { name: 'game-controller-outline', color: '#EC4899' },
  coffee: { name: 'cafe-outline', color: '#78350F' },
  shopping: { name: 'bag-outline', color: '#A855F7' },
  attractions: { name: 'library-outline', color: '#EF4444' },
  landmarks: { name: 'diamond-outline', color: '#D946EF' },
  parking: { name: 'car-outline', color: '#6B7280' },
  gas: { name: 'speedometer-outline', color: '#FBBF24' },
  hospital: { name: 'medical-outline', color: '#DC2626' },
  atm: { name: 'cash-outline', color: '#059669' },
  information: { name: 'information-circle-outline', color: '#2563EB' },
  nightlife: { name: 'wine-outline', color: '#7C3AED' },
  generic: { name: 'location-outline', color: '#6B7280' },
};

const STRUCTURED_CATEGORY_MAP: Record<string, PinVisualCategory> = {
  flight: 'flights',
  hotel: 'accommodation',
  lodging: 'accommodation',
  restaurant: 'food',
  activity: 'activities',
  attraction: 'attractions',
  museum: 'attractions',
  landmark: 'attractions',
  viewpoint: 'attractions',
  shopping: 'shopping',
  transportation: 'transportation',
  transport: 'transportation',
  car: 'transportation',
  bus: 'transportation',
  themepark: 'themeParks',
  themeparks: 'themeParks',
  park: 'themeParks',
};

const KEYWORD_GROUPS: KeywordGroup[] = [
  {
    category: 'activities',
    keywords: [
      'balloon',
      'hot air balloon',
      'balloon ride',
    ],
  },
  {
    category: 'accommodation',
    keywords: [
      'hotel',
      'lodging',
      'air-bnb',
      'air bnb',
      'airbnb',
      'hostel',
      'stays',
      'motel',
      'resort',
      'inn',
      'bed and breakfast',
      'b&b',
      'guesthouse',
      'vacation rental',
      'apartment',
      'suite',
      'room',
    ],
  },
  {
    category: 'ferry',
    keywords: [
      'ferry',
      'ferry terminal',
      'boat dock',
      'foot passenger',
    ],
  },
  {
    category: 'transportation',
    keywords: [
      'bus',
      'train',
      'subway',
      'metro',
      'tram',
      'light rail',
      'railway',
      'station',
      'transit',
      'public transport',
      'commuter rail',
      'monorail',
      'streetcar',
      'bus stop',
      'transit center',
    ],
  },
  {
    category: 'flights',
    keywords: [
      'plane',
      'airport',
      'flight',
      'airline',
      'terminal',
      'aviation',
      'air travel',
      'departure',
      'arrival',
      'gate',
      'airfield',
      'aerodrome',
      'international airport',
      'domestic flight',
    ],
  },
  {
    category: 'food',
    keywords: [
      'restaurant',
      'dining',
      'food',
      'eatery',
      'bistro',
      'cafe with food',
      'diner',
      'buffet',
      'steakhouse',
      'seafood',
      'fine dining',
      'casual dining',
      'fast casual',
      'lunch',
      'dinner',
      'brunch',
      'meal',
    ],
  },
  {
    category: 'activities',
    keywords: [
      'museum',
      'gallery',
      'art',
      'exhibition',
      'cultural center',
      'history museum',
      'science museum',
      'activities',
      'tours',
      'experiences',
      'attraction',
      'art gallery',
      'exhibit',
    ],
  },
  {
    category: 'themeParks',
    keywords: [
      'theme park',
      'amusement park',
      'rides',
      'roller coaster',
      'water park',
      'adventure park',
      'entertainment park',
      'fairground',
      'carnival',
      'family fun center',
    ],
  },
  {
    category: 'coffee',
    keywords: [
      'coffee',
      'cafe',
      'cafes',
      'coffee shop',
      'espresso',
      'latte',
      'cappuccino',
      'coffeehouse',
      'tea house',
      'juice bar',
      'smoothie bar',
      'bakery cafe',
    ],
  },
  {
    category: 'shopping',
    keywords: [
      'shopping',
      'mall',
      'store',
      'shop',
      'boutique',
      'market',
      'retail',
      'shopping center',
      'outlet',
      'plaza',
      'bazaar',
      'marketplace',
      'department store',
      'souvenir shop',
    ],
  },
  {
    category: 'attractions',
    keywords: [
      'attractions',
      'landmark',
      'monument',
      'tourist spot',
      'sightseeing',
      'viewpoint',
      'observation deck',
      'historic site',
      'point of interest',
      'scenic spot',
      'memorial',
      'statue',
      'tower',
    ],
  },
  {
    category: 'parking',
    keywords: [
      'parking',
      'parking lot',
      'parking garage',
      'car park',
      'valet',
      'parking structure',
      'paid parking',
      'free parking',
      'street parking',
      'parking meter',
    ],
  },
  {
    category: 'gas',
    keywords: [
      'gas',
      'fuel',
      'gas station',
      'petrol',
      'filling station',
      'fuel station',
      'service station',
      'charging station',
      'ev charging',
      'petrol pump',
    ],
  },
  {
    category: 'hospital',
    keywords: [
      'hospital',
      'medical',
      'clinic',
      'emergency',
      'doctor',
      'healthcare',
      'urgent care',
      'medical center',
      'pharmacy with clinic',
      'emergency room',
      'er',
      'health clinic',
      'medical facility',
    ],
  },
  {
    category: 'atm',
    keywords: [
      'atm',
      'bank',
      'banking',
      'cash',
      'money exchange',
      'currency exchange',
      'financial services',
      'cash machine',
      'money transfer',
      'bureau de change',
    ],
  },
  {
    category: 'information',
    keywords: [
      'information',
      'info',
      'tourist information',
      'help desk',
      'visitor center',
      'tourism office',
      'information center',
      'concierge',
      'travel desk',
    ],
  },
  {
    category: 'nightlife',
    keywords: [
      'nightlife',
      'bar',
      'pub',
      'club',
      'nightclub',
      'lounge',
      'cocktail bar',
      'wine bar',
      'tavern',
      'brewery',
      'dive bar',
      'dance club',
      'live music',
      'entertainment',
    ],
  },
];

const HIGHLIGHT_COLORS: Record<HighlightType, string> = {
  must_see: '#E67E22',
  trending: '#9B59B6',
};

const SIZE_CONFIG: Record<MarkerSize, SizeConfig> = {
  // Keep teardrop proportions but slightly smaller for a cleaner look
  small: { width: 30, height: 44 },
  medium: { width: 36, height: 52 },
  large: { width: 42, height: 62 },
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const matchesKeyword = (text: string, keyword: string) => {
  if (!keyword.trim()) return false;
  const pattern = new RegExp(`(?:^|[^a-z0-9])${escapeRegExp(keyword)}(?=$|[^a-z0-9])`, 'i');
  return pattern.test(text);
};

const collectTextFromPin = (pin: MapPin): string => {
  const parts: string[] = [];
  const push = (value?: string | null) => {
    if (value && typeof value === 'string') {
      parts.push(value);
    }
  };

  push(pin.title);
  push(pin.subtitle);
  if (pin.badges) {
    push(pin.badges.join(' '));
  }

  const data: any = pin.data;
  if (data) {
    push(data.description);
    push(data.address);
    push(data.location);
    push(data.notes);
    push(data.name);
    if (Array.isArray(data.tags)) {
      push(data.tags.join(' '));
    }
  }

  return parts.join(' ').toLowerCase();
};

const getStructuredCategory = (pin: MapPin): PinVisualCategory | null => {
  if (pin.type === 'user') return 'information';
  if (pin.type === 'search') return 'information';
  if (pin.type === 'landmark') return 'landmarks';

  const categoryKey = pin.category?.toLowerCase();
  if (categoryKey && STRUCTURED_CATEGORY_MAP[categoryKey]) {
    return STRUCTURED_CATEGORY_MAP[categoryKey];
  }

  const data: any = pin.data;
  const dataCategory = data?.category?.toLowerCase();
  if (dataCategory && STRUCTURED_CATEGORY_MAP[dataCategory]) {
    return STRUCTURED_CATEGORY_MAP[dataCategory];
  }

  return null;
};

const matchKeywordCategory = (text: string): PinVisualCategory | null => {
  if (!text) return null;
  const searchable = `${text} ${text.replace(/[\-/]/g, ' ')}`;
  for (const group of KEYWORD_GROUPS) {
    if (group.keywords.some((keyword) => matchesKeyword(searchable, keyword))) {
      return group.category;
    }
  }
  return null;
};

export const resolvePinCategory = (pin: MapPin): PinVisualCategory => {
  const structured = getStructuredCategory(pin);
  const keywordCategory = matchKeywordCategory(collectTextFromPin(pin));

  // Allow keyword-based category to override generic/activities so dining, lodging, transit etc. get the right icon
  if (keywordCategory && (!structured || structured === 'activities' || structured === 'generic')) {
    return keywordCategory;
  }

  if (structured) {
    return structured;
  }

  if (keywordCategory) {
    return keywordCategory;
  }

  return 'generic';
};

export const getPinIcon = (category: PinVisualCategory): { name: keyof typeof Ionicons.glyphMap; color: string } => {
  return PIN_ICONS[category] || PIN_ICONS.generic;
};

export const getPinColor = (pin: MapPin): string => {
  if (pin.markerColor) {
    return pin.markerColor;
  }
  if (pin.highlight) {
    const color = getHighlightColor(pin);
    if (color) return color;
  }
  switch (pin.type) {
    case 'itinerary':
      return '#1B5E20'; // primary green
    case 'landmark':
      return '#2563EB'; // accent blue for points of interest
    case 'user':
      return '#F59E0B'; // warm accent for current/user pins
    case 'search':
      return '#475569'; // neutral slate
    default:
      return '#475569';
  }
};

const getHighlightColor = (pin: MapPin): string | null => {
  if (!pin.highlight) return null;
  return HIGHLIGHT_COLORS[pin.highlight];
};

const useMarkerScale = (active: boolean) => {
  const scale = useRef(new Animated.Value(active ? 1.15 : 1)).current;

  useEffect(() => {
    Animated.timing(scale, {
      toValue: active ? 1.15 : 1,
      duration: 200,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [active, scale]);

  return scale;
};

// Pulse animation for highlighted pins
const usePulseAnimation = (highlighted: boolean) => {
  const pulse = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    if (highlighted) {
      const animation = Animated.loop(
        Animated.sequence([
          Animated.timing(pulse, {
            toValue: 1.08,
            duration: 1000,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
          Animated.timing(pulse, {
            toValue: 1,
            duration: 1000,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
        ])
      );
      animation.start();
      return () => animation.stop();
    } else {
      pulse.setValue(1);
    }
  }, [highlighted, pulse]);

  return pulse;
};

const getPinFillColor = (pin: MapPin, baseColor: string, highlightColor: string | null): string => {
  // Keep highlight as primary, otherwise use the per-pin base color.
  // This preserves category-specific pins while keeping the design flat/minimal.
  return highlightColor || baseColor;
};

export const MapMarker: React.FC<MapMarkerProps> = ({
  pin,
  size = 'medium',
  selected = false,
  showIndex = false,
  onPress,
}) => {
  const category = useMemo(() => resolvePinCategory(pin), [pin]);
  const pinIcon = useMemo(() => getPinIcon(category), [category]);
  const [pressed, setPressed] = useState(false);
  const scale = useMarkerScale(selected || pressed);
  const highlightColor = getHighlightColor(pin);
  const baseColor = useMemo(() => getPinColor(pin), [pin]);
  const config = SIZE_CONFIG[size];
  const iconSize = Math.floor(config.width * 0.44);
  const isHighlighted = !!highlightColor;
  const pulseScale = usePulseAnimation(isHighlighted);
  const fillColor = useMemo(
    () => getPinFillColor(pin, baseColor, highlightColor),
    [pin, baseColor, highlightColor],
  );
  const distanceMeta = pin.distanceMeta;
  const hasDistanceBadge = !!distanceMeta?.distanceFormatted;
  const showIndexChip = showIndex && pin.index !== undefined;
  // Keep badges/indices *above* the pin head without covering it.
  // (PointAnnotation marker views can clip negative offsets, so we reserve space instead.)
  const reserveTopChipSpace = hasDistanceBadge || showIndexChip;

  const content = (
    <Animated.View
      style={[
        styles.markerWrapper,
        {
          width: config.width,
          height: config.height,
          transform: [
            {
              // Subtle emphasis only; avoid the “bouncy” feel.
              scale: Animated.multiply(scale, isHighlighted ? pulseScale : 1),
            },
          ],
        },
        (selected || pressed) && styles.markerActive,
      ]}
    >
      {/* Flat, minimalist teardrop pin with category icon */}
      <View style={[styles.pinContainer, reserveTopChipSpace ? styles.pinContainerWithTopChip : null]}>
        <View style={[styles.pinHead, { backgroundColor: fillColor }, reserveTopChipSpace ? styles.pinHeadWithTopChip : null]}>
          <Ionicons name={pinIcon.name} size={iconSize} color="#FFFFFF" />
        </View>
        <View style={[styles.pinTail, { borderTopColor: fillColor }]} />
      </View>
      {hasDistanceBadge && distanceMeta?.distanceFormatted ? (
        <View
          style={[
            styles.distanceBadge,
            distanceMeta.distanceColor ? { borderColor: distanceMeta.distanceColor } : null,
          ]}
        >
          <Text style={styles.distanceBadgeText}>{distanceMeta.distanceFormatted}</Text>
        </View>
      ) : null}
      {highlightColor && (
        <View style={[styles.highlightDot, { backgroundColor: highlightColor }]}>
          {pin.highlight === 'must_see' ? (
            <Ionicons name="star" size={10} color="#FFFFFF" />
          ) : (
            <View style={styles.highlightDotInner} />
          )}
        </View>
      )}
      {showIndexChip && pin.index != null ? (
        <View style={[styles.indexBubble, hasDistanceBadge ? styles.indexBubbleBelowBadge : null]}>
          <Text style={styles.indexText}>{pin.index + 1}</Text>
        </View>
      ) : null}
    </Animated.View>
  );

  if (!onPress) {
    return content;
  }

  return (
    <Pressable
      onPress={onPress}
      onPressIn={() => setPressed(true)}
      onPressOut={() => setPressed(false)}
      style={styles.pressable}
    >
      {content}
    </Pressable>
  );
};

let webPinStylesInjected = false;

const ensureWebPinStyles = () => {
  if (webPinStylesInjected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.id = 'tripflow-pin-styles';
  style.innerHTML = `
    .tripflow-pin-wrapper {
      position: relative;
      width: 100%;
      height: 100%;
      transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), filter 0.2s ease;
      transform-origin: 50% 100%;
      filter: drop-shadow(0 3px 6px rgba(0,0,0,0.35)) drop-shadow(0 1px 2px rgba(0,0,0,0.2));
    }
    .tripflow-pin-wrapper.active {
      transform: translateY(-6px) scale(1.15);
      filter: drop-shadow(0 4px 8px rgba(0,0,0,0.4)) drop-shadow(0 2px 4px rgba(0,0,0,0.3)) drop-shadow(0 0 12px rgba(255,255,255,0.4));
    }
    .tripflow-pin-wrapper.highlight {
      animation: pulse-highlight 2s ease-in-out infinite;
    }
    @keyframes pulse-highlight {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.08); }
    }
    .tripflow-pin-wrapper.highlight::after {
      content: '';
      position: absolute;
      top: 4px;
      right: 4px;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      border: 2.5px solid #fff;
      background: var(--tripflow-highlight-color, #E67E22);
      box-shadow: 0 1px 3px rgba(0,0,0,0.3), 0 0 0 2px rgba(255,255,255,0.5);
      z-index: 10;
    }
    .tripflow-pin-wrapper.highlight::before {
      content: '';
      position: absolute;
      top: 6px;
      right: 6px;
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.8);
      z-index: 11;
    }
    .tripflow-pin-index {
      position: absolute;
      top: -10px;
      left: 50%;
      transform: translateX(-50%);
      min-width: 26px;
      padding: 3px 8px;
      border-radius: 999px;
      background: rgba(0,0,0,0.85);
      color: #fff;
      font-size: 12px;
      font-weight: 800;
      letter-spacing: 0.2px;
      text-align: center;
      pointer-events: none;
      border: 2px solid #fff;
      box-shadow: 0 2px 3px rgba(0,0,0,0.3);
      z-index: 10;
    }
    .tripflow-distance-badge {
      position: absolute;
      top: -14px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(18, 18, 18, 0.95);
      border-radius: 999px;
      padding: 4px 12px;
      color: #fff;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.3px;
      border: 2px solid #fff;
      box-shadow: 0 2px 3px rgba(0,0,0,0.3);
      pointer-events: none;
      white-space: nowrap;
      z-index: 10;
    }
  `;
  document.head.appendChild(style);
  webPinStylesInjected = true;
};

const setActiveState = (wrapper: HTMLElement, active: boolean) => {
  if (!wrapper) return;
  if (active) {
    wrapper.classList.add('active');
  } else {
    wrapper.classList.remove('active');
  }
};

// Web-specific asset path resolver - uses require to get bundled asset paths
const getWebAssetPath = (category: PinVisualCategory): string => {
  try {
    // Try to access PIN_ASSETS if it exists (might be defined elsewhere or imported)
    // For now, return empty string to use fallback markers
    // TODO: If PIN_ASSETS are available, uncomment this:
    // const { PIN_ASSETS } = require('./MapMarker') as any;
    // if (PIN_ASSETS && PIN_ASSETS[category]) {
    //   const resolved = Image.resolveAssetSource(PIN_ASSETS[category]);
    //   return resolved?.uri || '';
    // }
  } catch (error) {
    // Assets not available, will use fallback
  }
  return ''; // Always use fallback for now to ensure markers are visible
};

// Create fallback marker when assets aren't available
const createFallbackMarker = (
  wrapper: HTMLElement,
  category: PinVisualCategory,
  config: SizeConfig,
  color: string
) => {
  const pinIcon = PIN_ICONS[category] || PIN_ICONS.generic;
  const resolvedColor = color || pinIcon.color;
  
  // Create teardrop shape using CSS
  wrapper.style.width = `${config.width}px`;
  wrapper.style.height = `${config.height}px`;
  wrapper.style.position = 'relative';
  wrapper.style.display = 'flex';
  wrapper.style.flexDirection = 'column';
  wrapper.style.alignItems = 'center';
  wrapper.style.justifyContent = 'flex-start';
  
  // Pin head (circle) with gradient
  const pinHead = document.createElement('div');
  pinHead.style.width = `${Math.round(config.width * 0.75)}px`;
  pinHead.style.height = `${Math.round(config.width * 0.75)}px`;
  pinHead.style.borderRadius = '50%';
  // Use CSS gradient for web
  pinHead.style.background = `linear-gradient(135deg, ${resolvedColor} 0%, ${resolvedColor} 100%)`;
  pinHead.style.display = 'flex';
  pinHead.style.alignItems = 'center';
  pinHead.style.justifyContent = 'center';
  pinHead.style.marginTop = '4px';
  pinHead.style.boxShadow = '0 3px 6px rgba(0,0,0,0.35), 0 1px 2px rgba(0,0,0,0.2)';
  pinHead.style.border = '2.5px solid #fff';
  pinHead.style.position = 'relative';
  
  // Inner glow effect
  const innerGlow = document.createElement('div');
  innerGlow.style.position = 'absolute';
  innerGlow.style.top = '15%';
  innerGlow.style.left = '15%';
  innerGlow.style.width = '30%';
  innerGlow.style.height = '30%';
  innerGlow.style.borderRadius = '50%';
  innerGlow.style.background = 'rgba(255, 255, 255, 0.3)';
  innerGlow.style.pointerEvents = 'none';
  pinHead.appendChild(innerGlow);
  
  // Use emoji as icon inside pin head (with proper z-index)
  const icon = document.createElement('div');
  icon.textContent = getEmojiForCategory(category);
  icon.style.fontSize = `${Math.round(config.width * 0.35)}px`;
  icon.style.lineHeight = '1';
  icon.style.userSelect = 'none';
  icon.style.pointerEvents = 'none';
  icon.style.position = 'relative';
  icon.style.zIndex = '2';
  pinHead.appendChild(icon);
  
  // Pin tail (triangle pointing down) with enhanced shadow
  const pinTail = document.createElement('div');
  const tailSize = Math.round(config.width * 0.2);
  pinTail.style.width = '0';
  pinTail.style.height = '0';
  pinTail.style.borderLeft = `${tailSize}px solid transparent`;
  pinTail.style.borderRight = `${tailSize}px solid transparent`;
  pinTail.style.borderTop = `${Math.round(config.width * 0.25)}px solid ${resolvedColor}`;
  pinTail.style.marginTop = '-2px';
  pinTail.style.filter = 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))';
  pinTail.style.position = 'relative';
  pinTail.style.zIndex = '1';
  
  wrapper.appendChild(pinHead);
  wrapper.appendChild(pinTail);
};

// Get emoji for category as fallback
const getEmojiForCategory = (category: PinVisualCategory): string => {
  const emojiMap: Record<PinVisualCategory, string> = {
    accommodation: '🏨',
    transportation: '🚌',
    ferry: '⛴️',
    flights: '✈️',
    food: '🍽️',
    activities: '🎯',
    themeParks: '🎢',
    coffee: '☕',
    shopping: '🛍️',
    attractions: '📍',
    landmarks: '💎',
    parking: '🅿️',
    gas: '⛽',
    hospital: '🏥',
    atm: '🏦',
    information: 'ℹ️',
    nightlife: '🍸',
    generic: '📍',
  };
  return emojiMap[category] || '📍';
};

export const createWebMarkerElement = (
  pin: MapPin,
  size: MarkerSize = 'medium'
): HTMLDivElement => {
  ensureWebPinStyles();
  const category = resolvePinCategory(pin);
  const assetPath = getWebAssetPath(category);
  const highlightColor = getHighlightColor(pin);
  const baseColor = getPinColor(pin);
  const resolvedColor = highlightColor || baseColor;
  const config = SIZE_CONFIG[size];

  const container = document.createElement('div');
  container.className = 'tripflow-pin-container';
  container.style.width = `${config.width}px`;
  container.style.height = `${config.height}px`;
  container.style.position = 'relative';
  container.style.cursor = 'pointer';
  container.style.pointerEvents = 'auto';
  container.style.display = 'block';
  container.style.visibility = 'visible';
  container.style.opacity = '1';
  container.style.zIndex = '1000';

  const wrapper = document.createElement('div');
  wrapper.className = 'tripflow-pin-wrapper';
  // Ensure wrapper is visible
  wrapper.style.width = '100%';
  wrapper.style.height = '100%';
  wrapper.style.display = 'block';
  wrapper.style.visibility = 'visible';
  wrapper.style.opacity = '1';
  wrapper.style.position = 'relative';
  if (highlightColor) {
    wrapper.classList.add('highlight');
    wrapper.style.setProperty('--tripflow-highlight-color', highlightColor);
  }

  // Load the actual SVG asset or create fallback marker
  if (assetPath) {
    const img = document.createElement('img');
    img.src = assetPath;
    img.alt = pin.title || 'Map pin';
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.display = 'block';
    img.style.pointerEvents = 'none';
    img.draggable = false;
    img.onerror = () => {
      // If image fails to load, create fallback
      img.style.display = 'none';
      createFallbackMarker(wrapper, category, config, resolvedColor);
    };
    wrapper.appendChild(img);
  } else {
    // No asset path available, create fallback marker
    createFallbackMarker(wrapper, category, config, resolvedColor);
  }

  if (pin.distanceMeta?.distanceFormatted) {
    const distanceBadge = document.createElement('div');
    distanceBadge.className = 'tripflow-distance-badge';
    distanceBadge.textContent = pin.distanceMeta.distanceFormatted;
    if (pin.distanceMeta.distanceColor) {
      distanceBadge.style.borderColor = pin.distanceMeta.distanceColor;
    }
    wrapper.appendChild(distanceBadge);
  }

  if (pin.index !== undefined) {
    const indexChip = document.createElement('div');
    indexChip.className = 'tripflow-pin-index';
    indexChip.textContent = `${pin.index + 1}`;
    wrapper.appendChild(indexChip);
  }

  container.appendChild(wrapper);

  const activate = () => setActiveState(wrapper, true);
  const deactivate = () => setActiveState(wrapper, false);

  container.addEventListener('mouseenter', activate);
  container.addEventListener('mouseleave', deactivate);
  container.addEventListener('touchstart', activate, { passive: true });
  container.addEventListener('touchend', deactivate);
  container.addEventListener('touchcancel', deactivate);

  return container;
};

const styles = StyleSheet.create({
  pressable: {
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  markerWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    // Avoid clipping badges/chips inside native Mapbox annotations.
    overflow: 'visible',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.18,
        shadowRadius: 4,
      },
      android: {
        elevation: 4,
      },
      default: {},
    }),
  },
  markerActive: {
    ...Platform.select({
      ios: {
        shadowOpacity: 0.26,
        shadowRadius: 6,
        shadowOffset: { width: 0, height: 3 },
      },
      android: {
        elevation: 5,
      },
      default: {},
    }),
  },
  pinContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-start',
    position: 'relative',
  },
  pinContainerWithTopChip: {
    paddingTop: 22,
  },
  pinHead: {
    width: '75%',
    aspectRatio: 1,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 4,
    // Thin stroke for crispness (instead of a separate ring view)
    borderWidth: 1.5,
    borderColor: 'rgba(255, 255, 255, 0.85)',
    // Keep head crisp; shadows are handled at wrapper level.
    ...Platform.select({
      ios: {
        shadowOpacity: 0,
      },
      android: {
        elevation: 0,
      },
      default: {},
    }),
  },
  pinHeadWithTopChip: {
    marginTop: 0,
  },
  pinTail: {
    width: 0,
    height: 0,
    borderLeftWidth: 7,
    borderRightWidth: 7,
    borderTopWidth: 9,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    marginTop: -2,
    ...Platform.select({
      ios: {
        shadowOpacity: 0,
      },
      default: {},
    }),
  },
  highlightDot: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 12,
    height: 12,
    borderRadius: 6,
    borderWidth: 2.5,
    borderColor: '#FFFFFF',
    zIndex: 10,
    // Enhanced shadow for highlight
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.3,
        shadowRadius: 2,
      },
      android: {
        elevation: 4,
      },
      default: {},
    }),
  },
  highlightDotInner: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    transform: [{ translateX: -2 }, { translateY: -2 }],
  },
  indexBubble: {
    position: 'absolute',
    // Keep the index chip within the marker bounds (Mapbox marker views can clip negative offsets).
    top: 0,
    left: '50%',
    transform: [{ translateX: -14 }],
    minWidth: 26,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
    // Dark, high-contrast chip so numbers remain readable on light maps.
    backgroundColor: 'rgba(18,18,18,0.92)',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: '#FFFFFF',
    zIndex: 10,
    elevation: 10,
    // Enhanced shadow for index bubble
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.3,
        shadowRadius: 3,
      },
      android: {
        elevation: 5,
      },
      default: {},
    }),
  },
  indexBubbleBelowBadge: {
    top: 20,
  },
  indexText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '800',
    letterSpacing: 0.2,
    textAlign: 'center',
    // Helps avoid font clipping / invisible glyphs in some native snapshots.
    includeFontPadding: false,
    lineHeight: 16,
  },
  distanceBadge: {
    position: 'absolute',
    top: 0,
    alignSelf: 'center',
    backgroundColor: 'rgba(18,18,18,0.92)',
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderWidth: 2,
    borderColor: '#FFFFFF',
    zIndex: 10,
    elevation: 10,
    // Enhanced shadow for badge
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.3,
        shadowRadius: 3,
      },
      android: {
        elevation: 5,
      },
      default: {},
    }),
  },
  distanceBadgeText: {
    color: '#FFFFFF',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
});

export default MapMarker;
